import { z } from "zod";
import { DbUpdate, nullishToOptional } from "../helpers";
import { UserMinified } from "../users/user-minified";
import { OmitStrict, tryParseUrl } from "../utils";

export const IntegrationDesignSystem = z.object({
  designSystemId: z.string(),
  brandId: z.string(),
  title: z.string().optional(),
  userId: z.string().optional(),
  date: z.coerce.date().optional(),
});

export type IntegrationDesignSystem = z.infer<typeof IntegrationDesignSystem>;

export const IntegrationCredentialsType = z.enum(["OAuth2", "PAT"]);
export type IntegrationCredentialsType = z.infer<typeof IntegrationCredentialsType>;

export const IntegrationCredentialsState = z.enum(["Active", "Inactive"]);
export type IntegrationCredentialsState = z.infer<typeof IntegrationCredentialsState>;

export const IntegrationCredentialsProfile = z.object({
  id: nullishToOptional(z.string()),
  email: nullishToOptional(z.string()),
  handle: nullishToOptional(z.string()),
  type: nullishToOptional(z.string()),
  avatarUrl: nullishToOptional(z.string()),
  organization: nullishToOptional(z.string()),
  collection: nullishToOptional(z.string()),
});

export type IntegrationCredentialsProfile = z.infer<typeof IntegrationCredentialsProfile>;

export const IntegrationCredentials = z.object({
  id: z.string(),
  type: IntegrationCredentialsType,
  integrationId: z.string(),
  accessToken: z.string(),
  userId: z.string(),
  createdAt: z.coerce.date(),
  refreshToken: z.string().optional(),
  tokenName: z.string().optional(),
  expiresAt: z.coerce.date().optional(),
  refreshedAt: z.coerce.date().optional(),
  username: z.string().optional(),
  appInstallationId: z.string().optional(),
  profile: IntegrationCredentialsProfile.optional(),
  customUrl: z.string().optional(),
  state: IntegrationCredentialsState,

  user: UserMinified.optional(),
});

export type IntegrationCredentials = z.infer<typeof IntegrationCredentials>;

export type UpdateIntegrationCredential = OmitStrict<
  DbUpdate<IntegrationCredentials>,
  "id" | "integrationId" | "appInstallationId" | "type"
>;

export const ExtendedIntegrationType = z.enum([
  "Figma",
  "Github",
  "Gitlab",
  "Bitbucket",
  "Azure",
  "TokenStudio",
  "FigmaVariablesPlugin",
]);
export type ExtendedIntegrationType = z.infer<typeof ExtendedIntegrationType>;

export const IntegrationType = ExtendedIntegrationType.exclude(["TokenStudio", "FigmaVariablesPlugin"]);
export type IntegrationType = z.infer<typeof IntegrationType>;

export const GitIntegrationType = IntegrationType.exclude(["Figma"]);
export type GitIntegrationType = z.infer<typeof GitIntegrationType>;

export const Integration = z.object({
  id: z.string(),
  workspaceId: z.string(),
  type: IntegrationType,
  createdAt: z.coerce.date(),
  integrationCredentials: z.array(IntegrationCredentials).optional(),
});
export type Integration = z.infer<typeof Integration>;
export type ExtendedIntegration = Omit<Integration, "type"> & {
  type: ExtendedIntegrationType;
  integrationDesignSystems?: IntegrationDesignSystem[];
};

export const IntegrationToken = z
  .object({
    access_token: z.string(),
    refresh_token: z.string().optional(),
    expires_in: z.union([z.number().optional(), z.string().optional()]),
    token_type: z.string().optional(),
    token_name: z.string().optional(),
    token_azure_organization_name: z.string().optional(), // Azure Cloud PAT only
    token_azure_collection_name: z.string().optional(), // Azure Server PAT only
    token_bitbucket_username: z.string().optional(), // Bitbucket only
    custom_url: z
      .string()
      .optional()
      .transform(value => {
        if (!value?.trim()) return undefined;
        return formatCustomUrl(value);
      }),
  })
  .refine(data => {
    return !data.custom_url || tryParseUrl(data.custom_url);
  }, "Custom URL must be a valid URL")
  .refine(data => {
    if (data.custom_url && data.token_azure_organization_name) {
      return false;
    }
    return true;
  }, "Custom URL and Azure organization name cannot be present at the same time")
  .transform(data => {
    return {
      accessToken: data.access_token,
      refreshToken: data.refresh_token,
      expiresIn: typeof data.expires_in === "string" ? Number(data.expires_in) : data.expires_in,
      tokenType: data.token_type,
      tokenName: data.token_name,
      tokenBitbucketUsername: data.token_bitbucket_username,
      tokenAzureOrganizationName: data.token_azure_organization_name,
      tokenAzureCollection: data.token_azure_collection_name,
      customUrl: data.custom_url,
    };
  });

export type IntegrationToken = z.infer<typeof IntegrationToken>;

// Custom URL validation
const forbiddenCustomUrlDomainList = ["github.com", "gitlab.com", "bitbucket.org", "figma.com", "dev.azure.com"];

function formatCustomUrl(url: string): string | undefined {
  let formattedUrl = url.trim().toLowerCase();
  if (!formattedUrl.startsWith("http://") && !formattedUrl.startsWith("https://")) {
    formattedUrl = "https://" + formattedUrl;
  }
  if (formattedUrl.endsWith("/")) {
    formattedUrl = formattedUrl.slice(0, -1);
  }

  // do not store default urls that we incorrectly provided as custom urls
  return forbiddenCustomUrlDomainList.some(domain => formattedUrl.includes(domain)) ? undefined : formattedUrl;
}
